import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Badge } from '../../../../../components/ui/badge';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../components/ui/table';
import { cn } from '../../../../../lib/utils';
import { ArrowLeft, BarChart3, Plus } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';

export interface NDTInspectionModalProps {
  open: boolean;
  onClose: () => void;
  project?: DashboardProject;
  /** Selected spool from spool tracker (labels and status panel). */
  spoolName?: string;
}

export const NDTInspectionModal: React.FC<NDTInspectionModalProps> = ({ open, onClose, project, spoolName }) => {
  const [ndtSummaryOpen, setNdtSummaryOpen] = useState(false);
  const spoolLabel = spoolName?.trim() || 'SPOOL-3';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-screen h-screen max-w-none m-0 gap-0 border-0 bg-white p-0 shadow-none rounded-none [&>button]:hidden">
        <div className="flex h-screen flex-col overflow-hidden">
          <DialogHeader className="flex shrink-0 flex-row items-start justify-between gap-4 border-b bg-white px-4 py-4 sm:px-6">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={onClose} aria-label="Back">
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </Button>
              <div className="min-w-0">
                <DialogTitle className="text-left text-lg font-bold text-gray-900">NDT Inspection</DialogTitle>
                {project?.project?.projectName && (
                  <p className="mt-1 truncate text-sm text-gray-500">{project.project.projectName}</p>
                )}
                {spoolName?.trim() && (
                  <p className="mt-0.5 truncate text-sm font-medium text-gray-700">Spool: {spoolName.trim()}</p>
                )}
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {/* <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setNdtSummaryOpen((v) => !v)}
              >
                <BarChart3 className="h-4 w-4" />
                NDT Summary
              </Button> */}
              <Button type="button" size="sm" className="gap-1.5 bg-[#1E6353] text-white hover:bg-[#175544]">
                <Plus className="h-4 w-4" />
                Log NDT Result
              </Button>
              {/* <Button type="button" variant="outline" size="sm" onClick={onClose}>
                Close
              </Button> */}
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
            <div className="mx-auto max-w-full space-y-6">
              {/* {ndtSummaryOpen && (
                <div className="rounded-lg border border-blue-200 bg-blue-50/80 px-4 py-3 text-sm text-blue-950">
                  <strong>NDT Summary</strong> — Mock view: RT/UT pass rates by spool can be wired to production data later.
                </div>
              )} */}

              {/* <div className="rounded-lg border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-950">
                <p className="text-xs font-bold uppercase tracking-wide text-amber-800">Product Manager Note</p>
                <p className="mt-2 leading-relaxed">
                  <strong>Replaces</strong>: Paper NDT reports, manual repair tracking, handwritten RT/UT logs.
                  <br />
                  <strong>Key users</strong>: NDT Operator (Level II), QC Inspector (oversight).
                  <br />
                  <strong>Standards</strong>: ASME BPVC V, GIS 18-022. 100% RT + 100% UT on all butt welds.
                </p>
              </div> */}

              <div className="grid gap-6 lg:grid-cols-2">
                <Card className="border border-gray-200 shadow-sm">
                  <div className="flex flex-col gap-2 border-b bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                    <h3 className="text-base font-semibold text-gray-900">NDT Report Entry</h3>
                    <Badge variant="outline" className="w-fit border-orange-200 bg-orange-50 text-orange-900">
                      NDT Operator Level II
                    </Badge>
                  </div>
                  <CardContent className="space-y-4 p-4 sm:p-6">
                    <div className="grid gap-3 sm:grid-cols-2">
                      {/* <div className="space-y-1.5">
                        <Label className="text-xs text-gray-700">
                          Weld <span className="text-red-500">*</span>
                        </Label>
                        <Select defaultValue="w3">
                          <SelectTrigger className="h-9 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="w3">{spoolLabel} / W-3 — Awaiting NDT</SelectItem>
                            <SelectItem value="w2">CLOSURE-A-KIT / W-2 — Awaiting NDT</SelectItem>
                          </SelectContent>
                        </Select>
                      </div> */}
                      <div className="space-y-1.5">
                        <Label className="text-xs text-gray-700">
                          NDT Method <span className="text-red-500">*</span>
                        </Label>
                        <Select defaultValue="rt">
                          <SelectTrigger className="h-9 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="rt">RT — Radiographic Testing</SelectItem>
                            <SelectItem value="ut">UT — Ultrasonic Testing</SelectItem>
                            <SelectItem value="mpi">MPI — Magnetic Particle</SelectItem>
                            <SelectItem value="pt">PT — Penetrant Testing</SelectItem>
                            <SelectItem value="vt">VT — Visual Testing</SelectItem>
                            <SelectItem value="tofd">TOFD — Time of Flight Diffraction</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-gray-700">
                          Report Number <span className="text-red-500">*</span>
                        </Label>
                        <Input className="h-9 text-sm" placeholder="e.g. NDT-RT-2026-045" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-gray-700">Test Date</Label>
                        <Input className="h-9 text-sm" type="date" defaultValue="2026-04-06" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-gray-700">Equipment Calibration Ref</Label>
                        <Input className="h-9 text-sm" placeholder="Cal cert number" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-gray-700">Governing Standard</Label>
                        <Input className="h-9 bg-gray-50 text-sm" readOnly value="ASME BPVC V / GIS 18-022" />
                      </div>
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label className="text-xs text-gray-700">Acceptance Criteria</Label>
                        <Input className="h-9 text-sm" placeholder="Per governing standard" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-gray-700">
                          Result <span className="text-red-500">*</span>
                        </Label>
                        <Select defaultValue="pass">
                          <SelectTrigger className="h-9 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pass">Pass — Acceptable</SelectItem>
                            <SelectItem value="fail">Fail — Repair Required</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-800">
                        Indications Found
                      </h4>
                      {/* <div className="mb-3 rounded-md border border-amber-100 bg-amber-50/60 px-3 py-2 text-[11px] text-amber-950">
                        <span className="font-semibold text-amber-800">Structured for Analysis</span> — Each indication is
                        logged with clock position, type, dimensions, and disposition.
                      </div> */}
                      <div className="overflow-x-auto rounded-md border border-gray-200">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-gray-50 hover:bg-gray-50">
                              <TableHead className="whitespace-nowrap text-[10px] font-semibold">Clock</TableHead>
                              <TableHead className="whitespace-nowrap text-[10px] font-semibold">Dist. ref (mm)</TableHead>
                              <TableHead className="whitespace-nowrap text-[10px] font-semibold">Type</TableHead>
                              <TableHead className="whitespace-nowrap text-[10px] font-semibold">L (mm)</TableHead>
                              <TableHead className="whitespace-nowrap text-[10px] font-semibold">W (mm)</TableHead>
                              <TableHead className="whitespace-nowrap text-[10px] font-semibold">D (mm)</TableHead>
                              <TableHead className="whitespace-nowrap text-[10px] font-semibold">Orient.</TableHead>
                              <TableHead className="whitespace-nowrap text-[10px] font-semibold">Disposition</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            <TableRow>
                              <TableCell className="p-1">
                                <Select defaultValue="none">
                                  <SelectTrigger className="h-8 w-[95px] text-[11px]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none" className="text-xs">
                                      —
                                    </SelectItem>
                                    {['12', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'].map((h) => (
                                      <SelectItem key={h} value={h} className="text-xs">
                                        {h} o&apos;clock
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell className="p-1">
                                <Input className="h-8 w-[72px] px-2 text-[11px]" placeholder="mm" />
                              </TableCell>
                              <TableCell className="p-1">
                                <Select defaultValue="none">
                                  <SelectTrigger className="h-8 w-[130px] text-[11px]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none" className="text-xs">
                                      —
                                    </SelectItem>
                                    <SelectItem value="porosity" className="text-xs">
                                      Porosity
                                    </SelectItem>
                                    <SelectItem value="slag" className="text-xs">
                                      Slag inclusion
                                    </SelectItem>
                                    <SelectItem value="lof" className="text-xs">
                                      Lack of fusion
                                    </SelectItem>
                                    <SelectItem value="lop" className="text-xs">
                                      Lack of penetration
                                    </SelectItem>
                                    <SelectItem value="undercut" className="text-xs">
                                      Undercut
                                    </SelectItem>
                                    <SelectItem value="crack" className="text-xs">
                                      Crack
                                    </SelectItem>
                                    <SelectItem value="root" className="text-xs">
                                      Root concavity
                                    </SelectItem>
                                    <SelectItem value="other" className="text-xs">
                                      Other
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell className="p-1">
                                <Input className="h-8 w-14 px-1 text-[11px]" placeholder="mm" />
                              </TableCell>
                              <TableCell className="p-1">
                                <Input className="h-8 w-14 px-1 text-[11px]" placeholder="mm" />
                              </TableCell>
                              <TableCell className="p-1">
                                <Input className="h-8 w-14 px-1 text-[11px]" placeholder="mm" />
                              </TableCell>
                              <TableCell className="p-1">
                                <Select defaultValue="dash">
                                  <SelectTrigger className="h-8 w-[110px] text-[11px]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="dash">—</SelectItem>
                                    <SelectItem value="long">Longitudinal</SelectItem>
                                    <SelectItem value="trans">Transverse</SelectItem>
                                    <SelectItem value="cluster">Cluster</SelectItem>
                                    <SelectItem value="scat">Scattered</SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell className="p-1">
                                <Select defaultValue="acc">
                                  <SelectTrigger className="h-8 w-[110px] text-[11px]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="acc">Acceptable</SelectItem>
                                    <SelectItem value="rep">Repair required</SelectItem>
                                    <SelectItem value="rej">Reject</SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                      <Button type="button" variant="outline" size="sm" className="mt-2">
                        + Add Indication Row
                      </Button>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-700">Additional Notes</Label>
                      <Input className="h-9 text-sm" placeholder="Any observations not captured in the table above..." />
                    </div>

                    <div>
                      <h4 className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-slate-800">
                        Repair &amp; Retest Tracking
                      </h4>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label className="text-xs text-gray-700">Repair Reference</Label>
                          <Input className="h-9 text-sm" placeholder="Repair record ID (if repair needed)" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-gray-700">Retest NDT Report</Label>
                          <Input className="h-9 text-sm" placeholder="Link to follow-up NDT after repair" />
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-slate-800">BUYER Witness</h4>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label className="text-xs text-gray-700">BUYER Witnessed</Label>
                          <Select defaultValue="no">
                            <SelectTrigger className="h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="no">No</SelectItem>
                              <SelectItem value="yes">Yes</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-gray-700">BUYER Witness Name</Label>
                          <Select defaultValue="none">
                            <SelectTrigger className="h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">— Select —</SelectItem>
                              <SelectItem value="mt">M. Thompson (Subsea 7 QC)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 rounded-lg border border-dashed border-gray-300 bg-gray-50/80 px-4 py-6 text-center text-sm text-gray-600">
                      <p className="font-medium text-gray-800">Upload NDT Report / Radiograph / UT Scan</p>
                      <p className="mt-1 text-xs text-gray-500">
                        Attach radiograph image, UT scan data, TOFD record, or signed report PDF
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-700">Notes</Label>
                      <textarea
                        className={cn(
                          'min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                        )}
                        placeholder="Additional observations..."
                      />
                    </div>

                    <div className="flex justify-end gap-2 border-t pt-4">
                      <Button type="button" variant="outline">
                        Save Draft
                      </Button>
                      <Button type="button" className="bg-emerald-600 text-white hover:bg-emerald-700">
                        Submit NDT Report
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <div className="space-y-4">
                  <Card className="border border-gray-200 shadow-sm">
                    <div className="border-b bg-gray-50 px-4 py-3 sm:px-6">
                      <h3 className="text-base font-semibold text-gray-900">NDT Status — {spoolLabel}</h3>
                    </div>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50 hover:bg-gray-50">
                            <TableHead className="text-xs font-semibold">Weld</TableHead>
                            <TableHead className="text-xs font-semibold">RT</TableHead>
                            <TableHead className="text-xs font-semibold">UT</TableHead>
                            <TableHead className="text-xs font-semibold">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {[
                            { w: 'W-1', rt: 'pass', ut: 'pass', st: 'Accepted', sv: 'pass' as const },
                            { w: 'W-2', rt: 'pass', ut: 'pass', st: 'Accepted', sv: 'pass' as const },
                            { w: 'W-3', rt: 'pend', ut: 'pend', st: 'Awaiting', sv: 'warn' as const },
                            { w: 'W-4', rt: '—', ut: '—', st: 'Not welded', sv: 'draft' as const },
                          ].map((row) => (
                            <TableRow key={row.w}>
                              <TableCell className="font-mono text-sm font-medium">{row.w}</TableCell>
                              <TableCell>
                                {row.rt === '—' ? (
                                  '—'
                                ) : (
                                  <Badge
                                    className={
                                      row.rt === 'pass'
                                        ? 'border-transparent bg-emerald-50 text-emerald-800'
                                        : 'border-transparent bg-amber-50 text-amber-900'
                                    }
                                  >
                                    {row.rt === 'pass' ? 'Pass' : 'Pending'}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                {row.ut === '—' ? (
                                  '—'
                                ) : (
                                  <Badge
                                    className={
                                      row.ut === 'pass'
                                        ? 'border-transparent bg-emerald-50 text-emerald-800'
                                        : 'border-transparent bg-amber-50 text-amber-900'
                                    }
                                  >
                                    {row.ut === 'pass' ? 'Pass' : 'Pending'}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  className={
                                    row.sv === 'pass'
                                      ? 'border-transparent bg-emerald-50 text-emerald-800'
                                      : row.sv === 'warn'
                                        ? 'border-transparent bg-amber-50 text-amber-900'
                                        : 'border-transparent bg-slate-100 text-slate-700'
                                  }
                                >
                                  {row.st}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  {/* <Card className="border border-gray-200 shadow-sm">
                    <div className="border-b bg-gray-50 px-4 py-3 sm:px-6">
                      <h3 className="text-base font-semibold text-gray-900">NDT Requirements</h3>
                    </div>
                    <CardContent className="p-4 sm:p-6">
                      <div className="rounded-md border border-amber-100 bg-amber-50/60 px-3 py-3 text-sm text-amber-950">
                        <p className="text-xs font-bold uppercase text-amber-800">Per GIS 18-022</p>
                        <p className="mt-2 leading-relaxed">
                          <strong>100% RT</strong> on all butt welds
                          <br />
                          <strong>100% UT</strong> on all butt welds
                          <br />
                          <strong>100% MPI</strong> on all fillet welds
                          <br />
                          Each method requires its own report record. If fail → repair → retest (linked via retest_ndt_id).
                        </p>
                      </div>
                    </CardContent>
                  </Card> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
